import { COLORS } from '@/config/colors';
import { css, fillOutlined, circle, shade } from './draw-utils';
import type { ProceduralDef } from '../types';

const GLASS = 0x6fd0e8; // cyan crystal
const CORE = 0xffffff;

/**
 * Geometric Elemental — "Prism". A rotating crystalline polygon that fires
 * lattice/spoke patterns. Frame 0 calm / 1 enraged. docs/02 geometric line.
 */
export const bossPrismBody: ProceduralDef = {
  key: 'boss/prism/body',
  width: 150,
  height: 150,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 2,
  draw(ctx, w, h, frame) {
    const c = w / 2;
    const enraged = frame === 1;
    const tint = enraged ? COLORS.dangerRed : GLASS;

    // Outer glow
    const halo = ctx.createRadialGradient(c, c, 24, c, c, c);
    halo.addColorStop(0, enraged ? 'rgba(217,43,43,0.4)' : 'rgba(111,208,232,0.4)');
    halo.addColorStop(1, 'rgba(0,0,0,0)');
    circle(ctx, c, c, c);
    ctx.fillStyle = halo;
    ctx.fill();

    // Hexagonal crystal shell
    const hex = (r: number): void => {
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i - Math.PI / 2;
        const px = c + Math.cos(a) * r;
        const py = c + Math.sin(a) * r;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
    };
    hex(52);
    fillOutlined(ctx, shade(tint, 0.5), 6);
    hex(40);
    fillOutlined(ctx, tint, 4);

    // Inner facet lines
    ctx.strokeStyle = css(shade(tint, 1.4));
    ctx.lineWidth = 2.5;
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 3) * i - Math.PI / 2;
      ctx.beginPath();
      ctx.moveTo(c, c);
      ctx.lineTo(c + Math.cos(a) * 40, c + Math.sin(a) * 40);
      ctx.stroke();
    }

    // Bright core
    circle(ctx, c, c, enraged ? 16 : 13);
    fillOutlined(ctx, CORE, 3);
    if (enraged) {
      circle(ctx, c, c, 24);
      ctx.fillStyle = 'rgba(217,43,43,0.25)';
      ctx.fill();
    }
    void h;
  },
};
